import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildPaymentDepositExportRows,
  filterApprovedPaymentDeposits,
  getDefaultPaymentDepositFilters,
  getPaymentDepositDateTime,
  getPaymentDepositFilterOptions,
  getPaymentErpProcessingState,
  getPaymentDepositSourceLabel,
  groupPaymentDeposits,
  PaymentDepositFilters,
} from "./paymentDepositLedgerUtils";
import PaymentDepositCalendar from "./payment-deposit/PaymentDepositCalendar";

type DepositDraft = {
  erpRegistered: boolean;
  note: string;
};

type Props = {
  requests: any[];
  loading: boolean;
  error?: string;
  savingId?: string;
  onReload: (range: { dateFrom: string; dateTo: string }) => Promise<any>;
  onSave: (paymentRequestId: string, values: DepositDraft) => Promise<any>;
  onExcludeFromErp?: (
    paymentRequestId: string,
    options: { reclassifyLegacy: boolean }
  ) => Promise<any>;
  canExcludeFromErp?: boolean;
  onNotify?: (message: string) => void;
};

const getRequestId = (request: any) =>
  String(request.paymentRequestId || request.id || "");

const getInitialDraft = (request: any): DepositDraft => {
  const management = request.depositManagement || {};
  return {
    erpRegistered: management.erpRegistered === true,
    note: management.note || "",
  };
};

const PAYMENT_TYPE_OPTIONS = [
  ["ALL", "전체 카드결제"],
  ["BUYLINK", "문자링크"],
  ["KEYIN", "키인결제"],
  ["AUTOPAY", "등록카드"],
  ["STANDARD", "표준결제"],
];

const GROUP_OPTIONS = [
  ["NONE", "그룹핑 사용 안 함"],
  ["DATE", "거래일자별"],
  ["VENDOR", "거래처별"],
  ["MANAGER", "담당자별"],
  ["COMPANY", "거래사업자별"],
  ["CARD", "카드종류별"],
];

const SORT_OPTIONS = [
  ["NEWEST", "최신 거래순"],
  ["OLDEST", "오래된 거래순"],
  ["AMOUNT_DESC", "입금액 높은순"],
  ["AMOUNT_ASC", "입금액 낮은순"],
];

const ERP_PROCESSING_STATE_LABELS: Record<string, string> = {
  PENDING: "미처리",
  PROCESSING: "CN AX 처리중",
  VERIFIED: "ERP 완료",
  LEGACY_MANUAL_RECORDED: "기존 수동 기록",
  TEST_EXCLUDED: "테스트/ERP 제외",
};

const PaymentDepositLedger = ({
  requests,
  loading,
  error = "",
  savingId = "",
  onReload,
  onSave,
  onExcludeFromErp,
  canExcludeFromErp = false,
  onNotify,
}: Props) => {
  const [filters, setFilters] = useState<PaymentDepositFilters>(() =>
    getDefaultPaymentDepositFilters()
  );
  const [drafts, setDrafts] = useState<Record<string, DepositDraft>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [exclusionCandidate, setExclusionCandidate] = useState<{
    paymentRequestId: string;
    reclassifyLegacy: boolean;
  } | null>(null);
  const initialSearchStarted = useRef(false);

  useEffect(() => {
    if (initialSearchStarted.current) return;
    initialSearchStarted.current = true;
    void onReload({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
  }, [filters.dateFrom, filters.dateTo, onReload]);

  const filterOptions = useMemo(
    () => getPaymentDepositFilterOptions(requests),
    [requests]
  );
  const filtered = useMemo(
    () => filterApprovedPaymentDeposits(requests, filters),
    [filters, requests]
  );
  const groups = useMemo(
    () => groupPaymentDeposits(filtered, filters.groupBy),
    [filtered, filters.groupBy]
  );
  const totalAmount = useMemo(
    () =>
      filtered.reduce(
        (sum, request) =>
          sum + (Number(request.remainingAmount ?? request.amount) || 0),
        0
      ),
    [filtered]
  );
  const verifiedCount = filtered.filter(
    (request) => getPaymentErpProcessingState(request) === "VERIFIED"
  ).length;
  const pendingCount = filtered.filter(
    (request) => getPaymentErpProcessingState(request) === "PENDING"
  ).length;
  const canceledExclusionCandidates = useMemo(
    () =>
      canExcludeFromErp && onExcludeFromErp
        ? requests.filter(
            (request) =>
              String(request.status || "").toUpperCase() === "CANCELED"
          )
        : [],
    [canExcludeFromErp, onExcludeFromErp, requests]
  );

  const updateFilter = (field: keyof PaymentDepositFilters, value: string) => {
    setFilters((previous) => ({ ...previous, [field]: value }));
  };

  const updateDraft = (
    request: any,
    field: keyof DepositDraft,
    value: string | boolean
  ) => {
    const id = getRequestId(request);
    setDrafts((previous) => ({
      ...previous,
      [id]: {
        ...(previous[id] || getInitialDraft(request)),
        [field]: value,
      },
    }));
    setRowErrors((previous) => ({ ...previous, [id]: "" }));
  };

  const saveDraft = async (request: any) => {
    const id = getRequestId(request);
    if (!id || savingId) return;
    const draft = drafts[id] || getInitialDraft(request);
    try {
      await onSave(id, draft);
      setDrafts((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
      setRowErrors((previous) => ({ ...previous, [id]: "" }));
    } catch (saveError: any) {
      setRowErrors((previous) => ({
        ...previous,
        [id]: saveError?.message || "입금관리 정보를 저장하지 못했습니다.",
      }));
    }
  };

  const applyTestExclusion = async () => {
    const candidate = exclusionCandidate;
    if (!candidate || !onExcludeFromErp || savingId) return;
    try {
      await onExcludeFromErp(candidate.paymentRequestId, {
        reclassifyLegacy: candidate.reclassifyLegacy,
      });
      setExclusionCandidate(null);
      setRowErrors((previous) => ({
        ...previous,
        [candidate.paymentRequestId]: "",
      }));
    } catch (exclusionError: any) {
      setRowErrors((previous) => ({
        ...previous,
        [candidate.paymentRequestId]:
          exclusionError?.message || "테스트/ERP 제외를 완료하지 못했습니다.",
      }));
    }
  };

  const exportExcel = async () => {
    const rows = buildPaymentDepositExportRows(filtered);
    if (rows.length === 0) {
      onNotify?.("내보낼 승인 완료 입금내역이 없습니다.");
      return;
    }
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet["!cols"] = Object.keys(rows[0]).map((header) => ({
        wch: Math.min(Math.max(header.length + 4, 14), 42),
      }));
      XLSX.utils.book_append_sheet(workbook, sheet, "APPROVED_DEPOSITS");
      XLSX.writeFile(
        workbook,
        `cnfood_approved_deposits_${filters.dateFrom}_${filters.dateTo}.xlsx`
      );
      onNotify?.(`${rows.length}건의 승인 완료 입금내역을 저장했습니다.`);
    } catch {
      onNotify?.("입금관리 엑셀을 만들지 못했습니다.");
    }
  };

  const resetFilters = () => {
    const defaults = getDefaultPaymentDepositFilters();
    setFilters(defaults);
    void onReload({ dateFrom: defaults.dateFrom, dateTo: defaults.dateTo });
  };

  return (
    <section className="border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase text-emerald-700">
            Approved Deposit Ledger
          </p>
          <h4 className="mt-1 text-lg font-black text-slate-950">
            입금관리 현황
          </h4>
          <p className="mt-1 text-xs font-bold leading-5 text-slate-500">
            승인 완료 거래와 부분취소 후 승인잔액이 남은 거래만 표시합니다.
            미승인·실패·초안·완전취소 거래는 포함하지 않습니다.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              void onReload({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
              })
            }
            disabled={
              loading ||
              !filters.dateFrom ||
              !filters.dateTo ||
              filters.dateFrom > filters.dateTo
            }
            className="min-h-[42px] border border-slate-300 bg-white px-4 text-xs font-black text-slate-800 disabled:opacity-50"
          >
            {loading ? "조회 중..." : "다시 조회"}
          </button>
          <button
            type="button"
            onClick={() => void exportExcel()}
            disabled={filtered.length === 0}
            className="min-h-[42px] bg-emerald-700 px-4 text-xs font-black text-white disabled:bg-slate-300"
          >
            승인내역 Excel
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 bg-slate-50 p-3 sm:grid-cols-2 xl:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            승인일 시작
          </span>
          <input
            type="date"
            value={filters.dateFrom}
            max={filters.dateTo}
            onChange={(event) => updateFilter("dateFrom", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            승인일 종료
          </span>
          <input
            type="date"
            value={filters.dateTo}
            min={filters.dateFrom}
            onChange={(event) => updateFilter("dateTo", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            통합 검색
          </span>
          <input
            type="search"
            value={filters.query}
            onChange={(event) => updateFilter("query", event.target.value)}
            placeholder="거래처코드·거래처명·담당자·승인번호·거래번호·사업자·카드종류"
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            결제경로
          </span>
          <select
            value={filters.requestType}
            onChange={(event) => updateFilter("requestType", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            {PAYMENT_TYPE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            거래사업자
          </span>
          <select
            value={filters.paymentCompany}
            onChange={(event) =>
              updateFilter("paymentCompany", event.target.value)
            }
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            <option value="ALL">전체 사업자</option>
            {filterOptions.paymentCompanies.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            담당자
          </span>
          <select
            value={filters.createdBy}
            onChange={(event) => updateFilter("createdBy", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            <option value="ALL">전체 담당자</option>
            {filterOptions.createdBy.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            카드종류
          </span>
          <select
            value={filters.cardName}
            onChange={(event) => updateFilter("cardName", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            <option value="ALL">전체 카드</option>
            {filterOptions.cardNames.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            ERP 처리 상태
          </span>
          <select
            value={filters.erpRegistered}
            onChange={(event) =>
              updateFilter("erpRegistered", event.target.value)
            }
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            <option value="ALL">전체</option>
            <option value="PENDING">미처리</option>
            <option value="PROCESSING">CN AX 처리중</option>
            <option value="VERIFIED">ERP 완료</option>
            <option value="LEGACY_MANUAL_RECORDED">기존 수동 기록</option>
            <option value="TEST_EXCLUDED">테스트/ERP 제외</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            그룹핑
          </span>
          <select
            value={filters.groupBy}
            onChange={(event) => updateFilter("groupBy", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            {GROUP_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-black text-slate-500">
            정렬
          </span>
          <select
            value={filters.sort}
            onChange={(event) => updateFilter("sort", event.target.value)}
            className="min-h-[42px] w-full border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            {SORT_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() =>
              void onReload({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
              })
            }
            disabled={
              loading ||
              !filters.dateFrom ||
              !filters.dateTo ||
              filters.dateFrom > filters.dateTo
            }
            className="min-h-[42px] flex-1 bg-slate-900 px-3 text-xs font-black text-white disabled:bg-slate-300"
          >
            기간 검색
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="min-h-[42px] border border-slate-300 bg-white px-3 text-xs font-black text-slate-700"
          >
            오늘 ±2주
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="bg-slate-900 px-3 py-3 text-white">
          <p className="text-[11px] font-bold text-slate-300">승인 거래</p>
          <strong className="mt-1 block text-lg font-black">
            {filtered.length.toLocaleString("ko-KR")}건
          </strong>
        </div>
        <div className="bg-emerald-50 px-3 py-3 text-emerald-950">
          <p className="text-[11px] font-bold text-emerald-700">승인잔액 합계</p>
          <strong className="mt-1 block text-lg font-black">
            {totalAmount.toLocaleString("ko-KR")}원
          </strong>
        </div>
        <div className="bg-blue-50 px-3 py-3 text-blue-950">
          <p className="text-[11px] font-bold text-blue-700">ERP 완료</p>
          <strong className="mt-1 block text-lg font-black">
            {verifiedCount.toLocaleString("ko-KR")}건
          </strong>
        </div>
        <div className="bg-amber-50 px-3 py-3 text-amber-950">
          <p className="text-[11px] font-bold text-amber-700">미처리</p>
          <strong className="mt-1 block text-lg font-black">
            {pendingCount.toLocaleString("ko-KR")}건
          </strong>
        </div>
      </div>

      {canceledExclusionCandidates.length > 0 && (
        <section className="mt-3 border border-violet-200 bg-violet-50 px-3 py-3">
          <h3 className="text-xs font-black text-violet-950">
            완전취소 결제 테스트 제외 검토
          </h3>
          <p className="mt-1 text-[11px] font-bold text-violet-800">
            이름으로 자동 선택하지 않습니다. Owner가 payment ID와 금액을 확인해 정확히 한 건씩 선택합니다.
          </p>
          <div className="mt-2 space-y-2">
            {canceledExclusionCandidates.map((request) => {
              const id = getRequestId(request);
              const state = getPaymentErpProcessingState(request);
              const blocked = Boolean(
                request.erpBridge?.batchId ||
                  ["PROCESSING", "VERIFIED"].includes(state)
              );
              return (
                <div key={`canceled:${id}`} className="border border-violet-200 bg-white px-3 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] font-black text-slate-800">
                      {id} · {request.vendorBusinessName || request.customerName || "거래처 미확인"} · {Number(request.amount || 0).toLocaleString("ko-KR")}원 · 취소 {Number(request.cancellationHistory?.length || 0)}건
                    </p>
                    {state === "TEST_EXCLUDED" ? (
                      <span className="text-[11px] font-black text-violet-800">
                        테스트/ERP 제외
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={Boolean(savingId) || blocked}
                        onClick={() =>
                          setExclusionCandidate({
                            paymentRequestId: id,
                            reclassifyLegacy:
                              state === "LEGACY_MANUAL_RECORDED",
                          })
                        }
                        className="min-h-[34px] border border-violet-300 bg-violet-50 px-2 text-[11px] font-black text-violet-900 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        {state === "LEGACY_MANUAL_RECORDED"
                          ? "기존 수동기록 → 테스트 제외로 정정"
                          : "테스트/ERP 제외"}
                      </button>
                    )}
                  </div>
                  {exclusionCandidate?.paymentRequestId === id && (
                    <div className="mt-2 border-t border-violet-200 pt-2">
                      <p className="text-[11px] font-bold text-violet-900">
                        서버 transaction이 Batch·receipt·trigger·persistence가 모두 없음을 재검증합니다. 결제/Event 이력은 삭제하지 않습니다.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => setExclusionCandidate(null)}
                          className="min-h-[34px] border border-slate-300 bg-white px-3 font-black text-slate-700"
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(savingId)}
                          onClick={() => void applyTestExclusion()}
                          className="min-h-[34px] bg-violet-800 px-3 font-black text-white disabled:bg-slate-300"
                        >
                          테스트/ERP 제외 확정
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <PaymentDepositCalendar
        requests={filtered}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
      />

      {error && (
        <div className="mt-3 border border-red-200 bg-red-50 px-3 py-3 text-xs font-black text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center text-sm font-black text-slate-500">
          승인 완료 입금내역을 불러오는 중...
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-4 border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
          <p className="text-sm font-black text-slate-700">
            선택한 기간에 승인 완료된 입금내역이 없습니다.
          </p>
          <p className="mt-2 text-xs font-bold text-slate-500">
            미승인·실패·초안·완전취소 거래는 입금관리에서 표시하지 않습니다.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto border border-slate-200">
          <table className="min-w-[1540px] w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-slate-700">
              <tr>
                {[
                  "거래일자",
                  "거래시간",
                  "결제방법",
                  "거래처코드",
                  "거래처명",
                  "담당자",
                  "현재 승인잔액",
                  "승인번호",
                  "거래사업자",
                  "결제경로",
                  "카드종류",
                  "ERP 처리",
                  "기존 ERP 수동 등록 확인",
                  "비고",
                  "저장",
                ].map((header) => (
                  <th
                    key={header}
                    className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-black last:border-r-0"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((group) => {
                const rows: React.ReactNode[] = [];
                if (filters.groupBy !== "NONE") {
                  rows.push(
                    <tr key={`group:${group.key}`} className="bg-emerald-50">
                      <td
                        colSpan={15}
                        className="border-b border-emerald-200 px-3 py-2 font-black text-emerald-950"
                      >
                        {group.label} · {group.items.length}건 ·{" "}
                        {group.totalAmount.toLocaleString("ko-KR")}원
                      </td>
                    </tr>
                  );
                }
                group.items.forEach((request: any) => {
                  const id = getRequestId(request);
                  const approved = getPaymentDepositDateTime(request);
                  const draft = drafts[id] || getInitialDraft(request);
                  const erpProcessingState = getPaymentErpProcessingState(request);
                  const testExcluded = erpProcessingState === "TEST_EXCLUDED";
                  const exclusionBlocked = Boolean(
                    request.erpBridge?.batchId ||
                      ["PROCESSING", "VERIFIED"].includes(erpProcessingState)
                  );
                  const manager =
                    request.requestedByName ||
                    request.createdBy ||
                    request.updatedBy ||
                    "-";
                  rows.push(
                    <tr key={id} className="align-top hover:bg-slate-50">
                      <td className="border-b border-r border-slate-200 p-2">
                        <span className="inline-flex min-h-[38px] items-center whitespace-nowrap px-1 font-black text-slate-800">
                          {approved.date || "날짜 미확인"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {approved.time || "-"}
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-bold">
                        카드
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-black">
                        {request.vendorCode || "-"}
                      </td>
                      <td className="min-w-[180px] border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {request.vendorBusinessName ||
                          request.customerName ||
                          "거래처 미연결"}
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {manager}
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 text-right font-black">
                        {Number(
                          request.remainingAmount ?? request.amount
                        ).toLocaleString("ko-KR")}
                        원
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-black text-emerald-800">
                        {request.approvalCode || "-"}
                      </td>
                      <td className="min-w-[160px] border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {request.paymentCompanyName || "-"}
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {getPaymentDepositSourceLabel(request)}
                      </td>
                      <td className="whitespace-nowrap border-b border-r border-slate-200 px-3 py-3 font-bold">
                        {request.approvalCardName || "-"}
                      </td>
                      <td className="min-w-[150px] border-b border-r border-slate-200 px-3 py-3">
                        <span className="block font-black text-blue-800">
                          {ERP_PROCESSING_STATE_LABELS[erpProcessingState] ||
                            erpProcessingState ||
                            "미처리"}
                        </span>
                        <span className="mt-1 block break-all text-[11px] font-bold text-slate-500">
                          {testExcluded
                            ? `테스트 데이터 · ${request.erpExclusion?.excludedAt || "일시 미확인"}`
                            : request.erpBridge?.batchId || "Batch 없음"}
                        </span>
                      </td>
                      <td className="border-b border-r border-slate-200 px-3 py-3">
                        <label className="inline-flex min-h-[36px] items-center gap-2 whitespace-nowrap font-black">
                          <input
                            type="checkbox"
                            checked={draft.erpRegistered}
                            disabled={testExcluded}
                            onChange={(event) =>
                              updateDraft(
                                request,
                                "erpRegistered",
                                event.target.checked
                              )
                            }
                            className="h-4 w-4"
                          />
                          {draft.erpRegistered ? "수동 기록 있음" : "수동 기록 없음"}
                        </label>
                        <p className="mt-1 max-w-[230px] text-[10px] font-bold text-slate-500">
                          천년경영에 실제 수동 업로드가 완료된 경우에만 체크
                        </p>
                        {testExcluded ? (
                          <p className="mt-2 text-[11px] font-black text-violet-800">
                            테스트/ERP 제외 · {request.erpExclusion?.excludedBy || "담당자"}
                          </p>
                        ) : canExcludeFromErp && onExcludeFromErp ? (
                          <button
                            type="button"
                            disabled={Boolean(savingId) || exclusionBlocked}
                            onClick={() =>
                              setExclusionCandidate({
                                paymentRequestId: id,
                                reclassifyLegacy:
                                  erpProcessingState === "LEGACY_MANUAL_RECORDED",
                              })
                            }
                            className="mt-2 min-h-[34px] border border-violet-300 bg-violet-50 px-2 text-[11px] font-black text-violet-900 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                          >
                            {erpProcessingState === "LEGACY_MANUAL_RECORDED"
                              ? "기존 수동기록 → 테스트 제외로 정정"
                              : "테스트/ERP 제외"}
                          </button>
                        ) : null}
                      </td>
                      <td className="border-b border-r border-slate-200 p-2">
                        <input
                          value={draft.note}
                          maxLength={300}
                          onChange={(event) =>
                            updateDraft(request, "note", event.target.value)
                          }
                          placeholder="비고"
                          className="min-h-[38px] w-[200px] border border-slate-200 px-2 font-bold"
                        />
                        {rowErrors[id] && (
                          <p className="mt-1 max-w-[200px] text-[11px] font-black text-red-700">
                            {rowErrors[id]}
                          </p>
                        )}
                      </td>
                      <td className="border-b border-slate-200 p-2">
                        <button
                          type="button"
                          onClick={() => void saveDraft(request)}
                          disabled={Boolean(savingId)}
                          className="min-h-[38px] whitespace-nowrap bg-slate-900 px-3 font-black text-white disabled:bg-slate-300"
                        >
                          {savingId === id ? "저장 중" : "저장"}
                        </button>
                      </td>
                    </tr>
                  );
                  if (exclusionCandidate?.paymentRequestId === id) {
                    rows.push(
                      <tr key={`${id}:test-exclusion-confirmation`} className="bg-violet-50">
                        <td colSpan={15} className="border-b border-violet-200 px-4 py-3">
                          <p className="font-black text-violet-950">
                            이 결제를 테스트 데이터로 분류하고 CN AX Pending에서 제외할까요?
                          </p>
                          <p className="mt-1 text-[11px] font-bold text-violet-800">
                            서버가 Batch 없음, Import receipt/trigger/persistence 없음, 관련 Event가 PENDING인지 transaction에서 다시 확인합니다. 이력은 삭제하지 않습니다.
                          </p>
                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              onClick={() => setExclusionCandidate(null)}
                              className="min-h-[36px] border border-slate-300 bg-white px-3 font-black text-slate-700"
                            >
                              취소
                            </button>
                            <button
                              type="button"
                              disabled={Boolean(savingId)}
                              onClick={() => void applyTestExclusion()}
                              className="min-h-[36px] bg-violet-800 px-3 font-black text-white disabled:bg-slate-300"
                            >
                              테스트/ERP 제외 확정
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                });
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default PaymentDepositLedger;
